import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import Table from '@material-ui/core/Table'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  TableBody,
  TableRow,
  makeStyles,
} from '@material-ui/core'
import TableCell from 'components/Table/TableCell'
import {
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_METHOD,
  PaymentMethod,
  PREPAYMENT_SELECT,
  TARGET_NATION_OPTIONS,
  TARGET_TYPE,
  CHARGING_METHOD,
  USAGE_PERIOD_UNIT,
  CHARGING_ITEM,
} from 'pages/ProductsPages/constant'
import {AdminProductParamResponse, ChargeModeType} from 'api/productApi'
import ProductsDetailStyle from 'pages/ProductsPages/ProductsDetail/Style'

const useStyles = makeStyles(ProductsDetailStyle)

interface ProductCompositionProps {
  data: AdminProductParamResponse
  offerType: string
  disabledAll?: boolean
  setPaymentMethod: (key: PaymentMethod) => void
  setPrepaymentSelect: (value: string) => void
  setTargetNation: (value: string, key: string) => void
  setPeriodProduct: (value: string) => void
  unlimitPackage: boolean
  changeUnlimitPeriod: (value: boolean) => void
  setChargeMode: (value: ChargeModeType) => void
  setChargeQuantity: (value: number) => void
}

const ProductComposition = ({
  data,
  offerType,
  disabledAll = false,
  setPaymentMethod,
  setTargetNation,
  setPeriodProduct,
  setPrepaymentSelect,
  unlimitPackage,
  changeUnlimitPeriod,
  setChargeMode,
  setChargeQuantity,
}: ProductCompositionProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const [checked, setChecked] = React.useState(false)

  const {regionValue} = useMemo(() => {
    let org = ''
    let region = 'all'
    let en = ''
    let ko = ''

    const listKeyTarget = data.sale.saleTarget?.targets.map(
      (item) => item.targetType,
    )
    if (listKeyTarget?.indexOf(TARGET_TYPE.ORG_ID) !== -1) {
      setChecked(true)
    } else {
      setChecked(false)
    }

    data.sale.saleTarget?.targets.forEach((element) => {
      if (element.targetType === TARGET_TYPE.ORG_TYPE) {
        org = element.targetId
      } else if (element.targetType === TARGET_TYPE.REGION) {
        region = element.targetId
      }
    })
    data.goods.displayInfos.forEach((item) => {
      if (item.langCode === 'en') {
        en = item.name
      }
      if (item.langCode === 'ko') {
        ko = item.name
      }
    })
    return {
      orgValue: org,
      regionValue: region,
      nameProductEn: en,
      nameProductKo: ko,
    }
  }, [data])

  return (
    <Table size='small' className={classes.table}>
      <TableBody>
        <TableRow>
          <TableCell
            align='center'
            className={`${classes.cellParent}`}
            style={{
              width: '160px',
            }}
          >
            {t('IProductClassification')}
          </TableCell>
          <TableCell
            align='center'
            className={`${classes.headerMax}`}
            style={{
              width: 562,
            }}
          >
            <RadioGroup className={classes.groupRadio}>
              {PAYMENT_METHOD_OPTIONS.map((item) => {
                return (
                  <label
                    key={item.key}
                    htmlFor={item.key}
                    className={`${classes.fieldRadio} ${classes.monthlyInstallmentLabel}`}
                  >
                    <Radio
                      color='primary'
                      className={classes.p0}
                      id={item.key}
                      name={item.key}
                      value={item.key}
                      checked={data.sale.paymentMethod === item.key}
                      onChange={() => setPaymentMethod(item.key)}
                      disabled={disabledAll}
                    />
                    {t(item.label)}
                  </label>
                )
              })}
            </RadioGroup>
          </TableCell>
          <TableCell
            align='center'
            className={`${classes.cellParent}`}
            style={{
              width: '160px',
            }}
          >
            {t('INoAnalysisProvided')}
          </TableCell>
          <TableCell
            align='center'
            className={`${classes.headerMax}`}
            style={{
              width: 562,
            }}
          >
            <RadioGroup className={classes.groupRadio}>
              {PREPAYMENT_SELECT.map((item) => {
                return (
                  <label
                    key={item.id}
                    htmlFor={item.id}
                    className={`${classes.fieldRadio} ${classes.monthlyInstallmentLabel}`}
                  >
                    <Radio
                      color='primary'
                      className={classes.p0}
                      id={item.id}
                      name={item.id}
                      value={item.id}
                      checked={offerType === item.id}
                      onChange={() => setPrepaymentSelect(item.id)}
                      disabled={disabledAll}
                    />
                    {t(item.name)}
                  </label>
                )
              })}
            </RadioGroup>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            align='center'
            className={`${classes.cellParent}`}
            style={{
              width: '160px',
            }}
          >
            {t('IChargingMethod')}
          </TableCell>
          <TableCell
            align='center'
            className={`${classes.headerMax}`}
            style={{
              width: 562,
            }}
          >
            <RadioGroup
              className={classes.groupRadio}
              style={{
                width: 'fit-content',
              }}
            >
              {CHARGING_METHOD.map((item) => {
                return (
                  <label
                    key={item.id}
                    htmlFor={item.id}
                    className={`${classes.fieldRadio} ${classes.monthlyInstallmentLabel}`}
                  >
                    <Radio
                      color='primary'
                      className={classes.p0}
                      id={item.id}
                      name={item.id}
                      value={item.id}
                      checked={data.sale.chargeMode === item.id}
                      onChange={() => setChargeMode(item.id)}
                      disabled={disabledAll}
                    />
                    {t(item.name)}
                  </label>
                )
              })}
              <input
                className={classes.inputV2}
                style={{
                  width: 80,
                }}
                type='number'
                disabled={data.sale.chargeMode === CHARGING_ITEM.INDIVIDUAL}
                value={data.sale.chargeQuantity ?? 0}
                onChange={(e) => setChargeQuantity(Number(e.target.value))}
              />
            </RadioGroup>
          </TableCell>
          <TableCell
            align='center'
            className={classes.cellParent}
            style={{
              width: '160px',
            }}
          >
            {t('ITargetNation')}
          </TableCell>
          <TableCell
            align='center'
            className={classes.headerMax}
            style={{
              width: 562,
            }}
          >
            <div className={classes.groupRadioSpecial}>
              <FormControl variant='outlined' style={{marginLeft: 10}}>
                <Select
                  value={regionValue}
                  displayEmpty
                  onChange={(event) =>
                    setTargetNation(event.target.value as string, 'Region')
                  }
                  disabled={disabledAll}
                  className={classes.searchSelectV1}
                  input={<OutlinedInput />}
                >
                  {TARGET_NATION_OPTIONS?.map((item) => (
                    <MenuItem value={item.key} key={item.key}>
                      {t(item.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            align='center'
            className={`${classes.cellParent}`}
            style={{
              width: '160px',
            }}
          >
            {t('IAssignedByAdmin')}
          </TableCell>
          <TableCell
            className={`${classes.headerMax}`}
            style={{
              width: 562,
            }}
          >
            <FormControlLabel
              disabled={disabledAll}
              style={{marginLeft: 1}}
              control={
                <Checkbox
                  color='primary'
                  checked={checked}
                  onChange={() => setTargetNation('APPOINT', 'OrgId')}
                />
              }
              label={
                <h5
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                  }}
                >
                  {t('IAdminDesignAgency')}
                </h5>
              }
            />
          </TableCell>
          <TableCell
            align='center'
            className={`${classes.cellParent}`}
            style={{
              width: '160px',
            }}
          >
            {t('IPeriod')}
          </TableCell>

          <TableCell
            align='center'
            className={classes.headerMax}
            style={{
              width: 562,
            }}
          >
            <div className={classes.itemCell} style={{gap: '15px'}}>
              <div>
                <input
                  type='number'
                  className={classes.inputV2}
                  value={
                    data.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
                      ? ''
                      : data.offer.usagePeriod
                  }
                  onChange={(e) => setPeriodProduct(e.target.value)}
                  disabled={
                    disabledAll ||
                    data.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
                  }
                  min={0}
                />
                <span>{t('IMonths')}</span>
              </div>

              <FormControlLabel
                disabled={
                  data.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
                    ? false
                    : disabledAll
                }
                control={
                  <Checkbox
                    color='primary'
                    checked={unlimitPackage}
                    onChange={() => changeUnlimitPeriod(!unlimitPackage)}
                    name='unlimitPeriod'
                  />
                }
                label={
                  <h5 style={{fontSize: '0.8125rem', fontWeight: 400}}>
                    {t('INYears', {
                      number: 10,
                    })}
                  </h5>
                }
              />
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default ProductComposition
